/**
 * The basemap both maps draw on, in one place so the hero map and the search map cannot drift
 * apart on tiles or attribution.
 *
 * <p>Deliberately not OpenStreetMap's standard style. That style bakes POI symbols into the tile
 * images, and in Indian cities the densest of them by far is healthcare: every pharmacy, clinic
 * and hospital renders as a red circle with a white cross. A dozen of those inside a 5 km radius
 * read as pins somebody placed - they sit at the same size as our own dots and compete with them
 * for exactly the meaning our dots carry, worst of all on an empty map, where the only marks on
 * screen belong to the basemap and the count underneath says "0 listings".
 *
 * <p>Esri's light canvas carries no POI symbols at all - roads and street names only - so the only
 * coloured things left on the map are ours. It needs no API key, which is why it is here rather
 * than CARTO Positron: CARTO now watermarks keyless tiles with "API KEY REQUIRED". It is two
 * layers, both added, in this order: the Base (the fill - land, water, built-up areas) and the
 * Reference on top of it (the line work and street labels) - splitting them is Esri's own design
 * for this product, and the labels are too faint to read reliably off the Base alone.
 *
 * <p>The dark-canvas sibling of this product was tried first and was too dark in practice - this
 * is the light one instead, which reads closer to a conventional map. If it ever needs tuning
 * again, Esri also publishes this same pair in a dark variant (swap Light for Dark in both URLs
 * below) - no other change needed, since both share this file's layer order and attribution.
 *
 * <p>Attribution is a condition of use, not decoration. Leaflet renders it into the corner control
 * from the string below; do not drop it.
 */
export const ESRI_GREY_TILES =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}'

/** The street linework and labels layer - stack this on top of {@link ESRI_GREY_TILES}. */
export const ESRI_GREY_REFERENCE_TILES =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}'

/** Note the {z}/{y}/{x} order above - Esri puts row before column, the reverse of OSM's XYZ. */
export const ESRI_GREY_ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com/">Esri</a> - Esri, HERE, Garmin, ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
